import React from "react";
import { Loader } from "../../../../ui/components/Loader";
import { Button } from "../../../../ui/components/Button";
import { JournalCountsEntity, JournalListStatus } from "../../domain";

interface JournalStatsCardsProps {
  counts: JournalCountsEntity | null;
  loading: boolean;
  onSelectStatus: (status: JournalListStatus) => void;
  onManageRecurring: () => void;
}

export const JournalStatsCards: React.FC<JournalStatsCardsProps> = ({
  counts,
  loading,
  onSelectStatus,
  onManageRecurring,
}) => {
  const workflowCards = [
    {
      label: "Under Review",
      value: counts?.journals.underReview ?? 0,
      status: "under_review" as const,
      action: "View Under Review",
    },
    {
      label: "Approved",
      value: counts?.journals.approved ?? 0,
      status: "approved" as const,
      action: "View Approved",
    },
    {
      label: "Saved as Draft",
      value: counts?.journals.saveAsDraft ?? 0,
      status: "save_as_draft" as const,
      action: "View Drafts",
    },
    {
      label: "Rejected",
      value: counts?.journals.rejected ?? 0,
      status: "rejected" as const,
      action: "View Rejected",
    },
  ];

  const recurringCards = [
    { label: "Active", value: counts?.recurringTemplates.active ?? 0 },
    { label: "Paused", value: counts?.recurringTemplates.paused ?? 0 },
    { label: "Ended", value: counts?.recurringTemplates.ended ?? 0 },
  ];

  return (
    <div className="flex w-full flex-wrap gap-4 items-stretch">
      <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
        <div className="flex w-full items-center justify-between">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Journals
          </span>
        </div>
        {loading ? (
          <div className="flex w-full items-center justify-center py-4">
            <Loader />
          </div>
        ) : (
          <div className="flex w-full flex-wrap items-start gap-4">
            {workflowCards.map((card) => (
              <div
                key={card.status}
                className="flex grow shrink-0 basis-0 flex-col items-start gap-2"
              >
                <span className="text-body font-body text-subtext-color">
                  {card.label}
                </span>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {card.value}
                </span>
                <Button
                  variant="neutral-secondary"
                  size="small"
                  onClick={() => onSelectStatus(card.status)}
                >
                  {card.action}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex grow shrink-0 basis-0 flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-6 py-6">
        <div className="flex w-full items-center justify-between">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Recurring Journals
          </span>
        </div>
        {loading ? (
          <div className="flex w-full items-center justify-center py-4">
            <Loader />
          </div>
        ) : (
          <div className="flex w-full flex-wrap items-start gap-4">
            {recurringCards.map((card) => (
              <div
                key={card.label}
                className="flex grow shrink-0 basis-0 flex-col items-start gap-1"
              >
                <span className="text-body font-body text-subtext-color">
                  {card.label}
                </span>
                <span className="text-heading-2 font-heading-2 text-default-font">
                  {card.value}
                </span>
              </div>
            ))}
          </div>
        )}
        <Button size="small" onClick={onManageRecurring}>
          View and Manage
        </Button>
      </div>
    </div>
  );
};
